import { refreshAccessToken } from "@stryker/core/api/accounts";
import { addJobDocuments, deleteJobDocuments, getJob, getJobReport } from "@stryker/core/api/jobs";
import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { Job, LocalFile } from "@stryker/core/types/jobs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "remeda";
import AnalyticIcon from "@/assets/icons/analytic.svg?react";
import CopyIcon from "@/assets/icons/copy.svg?react";
import DotsVerticalIcon from "@/assets/icons/dots-vertical.svg?react";
import DownloadIcon from "@/assets/icons/download.svg?react";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import FileUploader from "@/components/FileUploader";
import AttachedDocument from "@/components/job/AttachedDocument";
import DeleteJobModal from "@/components/job/DeleteJobModal";
import DuplicateJobModal from "@/components/job/DuplicateJobModal";
import NoCreditsModal from "@/components/job/NoCreditsModal";
import Status from "@/components/job/Status";
import Notification from "@/components/Notification";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "@/components/ui/typography";
import { toast } from "@/components/ui/use-toast";
import useGetJobInfoBlocks from "@/hooks/useGetJobInfoBlocks";
import useOrganization from "@/hooks/useOrganization";
import useScreens from "@/hooks/useScreens";
import { isNumeric } from "@/utils/checks";
import { getJobTypeRoute } from "@/utils/navigation";
import { getAccessToken } from "@/utils/storageService";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

export default function Job() {
  const [showNoCredits, setShowNoCredits] = useState(false);
  const [jobIdToRemove, setJobIdToRemove] = useState<null | number>(null);
  const [jobIdToDuplicate, setJobIdToDuplicate] = useState<null | number>(null);
  const [reportError, setReportError] = useState("");
  const [newDocuments, setNewDocuments] = useState<Array<LocalFile>>([]);
  const [newReferenceDocuments, setNewReferenceDocuments] = useState<Array<LocalFile>>([]);

  useEffect(() => {
    return () => {
      setJobIdToRemove(null);
      setJobIdToDuplicate(null);
      setReportError("");
      setNewDocuments([]);
      setNewReferenceDocuments([]);
    };
  }, []);

  const { data: profilePermissions } = useQuery({
    ...profileQueryOptions(),
    select: (response) => ({
      isAdmin: profileSelectors.isAdmin(response),
      canRunCalculations: profileSelectors.canRunCalculations(response),
    }),
  });
  const isAdminRole = profilePermissions?.isAdmin;
  const canRunCalculations = profilePermissions?.canRunCalculations;
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  console.log("Job page loaded with id:", id, typeof id);
  const { data: job, isLoading } = useQuery({
    queryKey: [QueryKeys.Job, id],
    queryFn: () => getJob(id as string),
    enabled: !!id,
  });

  const {
    data: chatHistory,
    isLoading: isChatLoading,
    error: chatError,
    refetch: refetchChatHistory,
  } = useQuery<ChatMessage[], Error>({
    queryKey: ["chatHistory", id],
    queryFn: async () => {
      console.log("=== CHAT QUERY FN EXECUTED ==="); // Prominent log to confirm queryFn runs
      if (!import.meta.env.VITE_AI_AGENT_CHAT_ENDPOINT) {
        console.error(
          "VITE_AI_AGENT_CHAT_ENDPOINT is undefined. Please check your .env file and restart the dev server."
        );
        throw new Error("AI chat endpoint not configured");
      }
      const jobNumber = job!.job_number!;
      console.log("Fetching chat history for job:", jobNumber);
      const endpoint = `${import.meta.env.VITE_AI_AGENT_CHAT_ENDPOINT}${jobNumber}`;
      console.log("Chat endpoint:", endpoint);
      const initialToken = getAccessToken();
      if (!initialToken) {
        console.error("No auth token available");
        throw new Error("No auth token available. Please login.");
      }
      let currentToken = initialToken;

      const fetchChat = async (tok: string) => {
        return await fetch(endpoint, {
          method: "GET",
          mode: "cors",
          headers: {
            Authorization: `Bearer ${tok}`,
          },
        });
      };

      let res = await fetchChat(currentToken);

      if (res.status === 401) {
        await refreshAccessToken();
        const newToken = getAccessToken();
        if (!newToken) {
          throw new Error("Failed to refresh token. Please login again.");
        }
        currentToken = newToken;
        res = await fetchChat(currentToken);
      }

      console.log("Chat fetch response status:", res.status, res.statusText);
      if (!res.ok) {
        if (res.status === 404 || res.status === 403) {
          console.log(`No chat history found (${res.status}), returning empty array`);
          return [];
        }
        if (res.status === 401) {
          throw new Error("Session expired. Please login again.");
        }
        const errorText = await res.text();
        console.error("Chat fetch error:", errorText);
        throw new Error(errorText);
      }
      const contentType = res.headers.get("Content-Type");
      console.log("Response Content-Type:", contentType);
      if (!contentType?.includes("application/json")) {
        const responseText = await res.text();
        console.error("Non-JSON response received for chat history");
        console.log("Response body (first 200 chars):", responseText.substring(0, 200));
        throw new Error(`Expected JSON but got ${contentType || "no Content-Type"}. Check endpoint/auth.`);
      }
      const rawData = await res.json();

      console.log("Raw API response structure:", rawData); // Debug: Inspect exact response format
      let data: ChatMessage[] = [];
      if (typeof rawData === "object" && rawData !== null && "messages" in rawData && Array.isArray(rawData.messages)) {
        data = rawData.messages as ChatMessage[];
      } else {
        console.warn(
          "Invalid chat data format (missing or invalid 'messages' array), returning empty array. Raw data type:",
          typeof rawData,
          rawData
        );
      }
      console.log("Chat history data received:", data.length, data);
      return data;
    },
    enabled: !!job?.job_number,
  });

  useEffect(() => {
    if (chatError) {
      console.error("Chat history error details:", chatError); // Additional console log for full stack/cause
      toast({ variant: "destructive", description: `Chat load failed: ${chatError.message}` });
    }
  }, [chatError]);

  useEffect(() => {
    if (job?.job_number) {
      console.log("Job loaded, explicitly refetching chat history for:", job.job_number);
      refetchChatHistory();
    }
  }, [job?.job_number, refetchChatHistory]);

  useEffect(() => {
    console.log("Chat state update:", {
      isChatLoading,
      chatHistoryLength: chatHistory?.length ?? 0,
      hasHistory: (chatHistory ?? []).length > 0,
      chatHistory: chatHistory ?? [],
      error: chatError?.message || null,
      jobNumber: job?.job_number,
    });
  }, [isChatLoading, chatHistory, chatError, job?.job_number]);

  const { data: jobReport } = useQuery({
    queryKey: [QueryKeys.JobReport, id],
    queryFn: () => getJobReport(id as string),
    enabled: !!id && job?.job_status !== "DRAFT",
    refetchInterval: (query) => {
      const res = query.state.data;
      if (!res) {
        return 2000;
      }

      const status = res?.status;
      const result = res?.result;
      const error = res?.error;
      const hasResult = !!result && result !== "null";

      if (status === "Completed" || hasResult || error) {
        if (hasResult) {
          queryClient.invalidateQueries({ queryKey: [QueryKeys.Job, id] });
        }
        return false;
      }

      return 2000;
    },
  });

  const { data: organization } = useOrganization();
  const hasCredits = !!organization?.balanceValue;

  const { mutate: addJobDocumentsMutate } = useMutation({
    mutationFn: (data: FormData) => addJobDocuments(Number(id), data),
    onSuccess: () => {
      setNewDocuments([]);
      setNewReferenceDocuments([]);
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobReport, id] });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.toString() });
    },
  });

  const { mutate: deleteDocumentsMutate } = useMutation({
    mutationFn: deleteJobDocuments,
    onError: (error) => {
      toast({ variant: "destructive", description: error?.toString() });
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobReport, id] });
    },
  });

  const addNewDocs = () => {
    const formData = new FormData();
    newDocuments.forEach((doc) => formData.append("attached_documents", doc.file));
    newReferenceDocuments.forEach((doc) => formData.append("reference_documents", doc.file));
    addJobDocumentsMutate(formData);
  };

  const handleDuplicate = (id: number) => {
    if (hasCredits) {
      setJobIdToDuplicate(id);
    } else {
      setShowNoCredits(true);
    }
  };

  const jobInfo = useGetJobInfoBlocks({ job });

  useEffect(() => {
    if (jobReport?.error) {
      setReportError(jobReport.error);
    }
  }, [jobReport?.error]);

  const dropdownOptions = [
    {
      id: "copy",
      title: (
        <div className="flex gap-2">
          <CopyIcon />
          <Typography variant="text-small" weight="medium" className="text-left text-gray-700">
            Copy link for editing
          </Typography>
        </div>
      ),
      onClick: async () => {
        await copyDraftEditUrl();
      },
    },
    {
      id: "delete",
      title: (
        <div className="flex gap-2">
          <DeleteIcon />
          <Typography variant="text-small" weight="medium" className="text-left text-gray-700">
            Delete job
          </Typography>
        </div>
      ),
      onClick: () => {
        setJobIdToRemove(Number(id));
      },
    },
  ];

  const filteredOptions = dropdownOptions.filter((option) => {
    if (option.id === "delete") {
      return isAdminRole;
    }
    if (option.id === "copy") {
      return job?.job_status === "DRAFT";
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner svgClassName="h-20 w-20" />
      </div>
    );
  }

  const documentsToShow = job?.attached_documents ? [...newDocuments, ...job.attached_documents] : newDocuments;
  const attachedNotesToShow = job?.reference_documents
    ? [...newReferenceDocuments, ...job.reference_documents]
    : newReferenceDocuments;

  const viewReport = () => {
    if (jobReport?.result) {
      window.open(jobReport.result as string, "_blank")?.focus();
    }
  };

  const downloadReport = () => {
    if (jobReport?.result) {
      fetch(jobReport.result, {
        method: "GET",
        headers: {},
      })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${job?.job_number}_${job?.plant_name}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        });
    }
  };

  const copyDraftEditUrl = async () => {
    try {
      const jobTypeRoute = job?.job_type ? getJobTypeRoute(job.job_type) : "composite";
      const editLocation = `${window.location.origin}/jobs/create/${jobTypeRoute}?jobId=${id}&jobName=${encodeURIComponent(job?.job_name || "")}`;
      await navigator.clipboard.writeText(editLocation);
      toast({ description: "Link copied to clipboard" });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const allowedStatusesForDuplicate: Array<Job["job_status"] | string> = ["Completed", "FAILED"];
  const shouldShowDuplicateJob =
    allowedStatusesForDuplicate.includes(job?.job_status!) || allowedStatusesForDuplicate.includes(jobReport?.status!);

  return (
    <div className="relative mb-40 pb-4 md:mb-0">
      <div className="flex items-center gap-5">
        <Link to="/jobs">
          <Typography className="text-gray-500" weight="medium">
            Jobs
          </Typography>
        </Link>
        <Typography className="text-gray-500">/</Typography>
        <Typography className="text-primary-500" variant="text-small" weight="semibold">
          {job?.job_name}
        </Typography>
      </div>
      <div className="my-4 flex flex-wrap items-center justify-end gap-2">
        {jobReport?.result && (
          <>
            <Button variant="secondary" className="w-full md:w-fit" onClick={viewReport}>
              <AnalyticIcon className="me-2" />
              View as PDF file
            </Button>
            <Button className="w-full md:w-fit" onClick={downloadReport}>
              <DownloadIcon />
              Download
            </Button>
          </>
        )}
        {shouldShowDuplicateJob && (
          <Button
            className="flex w-full gap-2 md:w-fit"
            onClick={() => {
              handleDuplicate(Number(id));
            }}
          >
            <CopyIcon className="text-white" />
            Duplicate
          </Button>
        )}
        <div className="flex items-center gap-2">
          {job?.job_status === "DRAFT" && canRunCalculations && (
            <Button
              className="w-full md:w-[200px]"
              onClick={() => {
                const jobTypeRoute = job?.job_type ? getJobTypeRoute(job.job_type) : "composite";
                navigate(`/jobs/create/${jobTypeRoute}?jobId=${id}&jobName=${encodeURIComponent(job?.job_name || "")}`);
              }}
            >
              Finish
            </Button>
          )}
          <Status status={jobReport?.status} innerContainerClassName="h-10" />
          {filteredOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <div className="cursor-pointer p-4">
                  <DotsVerticalIcon />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {filteredOptions.map((option, index) => {
                  return (
                    <DropdownMenuLabel className="cursor-pointer" key={index} onClick={option.onClick}>
                      {option.title}
                    </DropdownMenuLabel>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {jobInfo.map((detail: any) => {
          if (isEmpty(detail.content)) {
            return null;
          }
          return <JobCard key={detail.title} title={detail.title} content={detail.content} />;
        })}
      </div>
      {isChatLoading ? (
        <div className="flex w-full justify-center p-4">
          <Spinner svgClassName="h-8 w-8" />
        </div>
      ) : (chatHistory ?? []).length > 0 ? (
        <div className="mt-4 min-w-full overflow-hidden rounded-[10px] border border-success-300 border-solid p-6">
          <Typography variant="text-xl" weight="semibold" className="mb-4 text-gray-800">
            AI Agent Chat History
          </Typography>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {(chatHistory ?? []).map((message: ChatMessage, index: number) => (
              <div
                key={index}
                className={`rounded-lg p-3 ${
                  message.role === "assistant" ? "bg-blue-50 text-blue-800" : "bg-gray-50 text-gray-800"
                }`}
              >
                <Typography variant="text-small" weight="medium" className="mb-1">
                  {message.role === "user" ? "User" : "Assistant"}:
                </Typography>
                <Typography variant="text-small" className="whitespace-pre-wrap">
                  {message.content.replace(/\\"/g, '"').replace(/^"(.*)"$/, "$1")}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mt-8 flex flex-col px-6">
        <Typography variant="text-lg" weight="semibold" className="mb-6 border-gray-200 border-b border-solid pb-5">
          Attach to report/ reference documents
        </Typography>
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="flex-1 md:w-1/2">
            <Typography className="text-center" variant="display-xs" weight="semibold">
              Attach to Report
            </Typography>
            <div className="flex flex-wrap gap-4">
              {documentsToShow.map((d, index) => {
                const isSavedDoc = "link" in d;
                return (
                  <AttachedDocument
                    key={index}
                    document={d}
                    onClick={() => {
                      if (isSavedDoc) {
                        window.open(d.link, "_blank")?.focus();
                      }
                    }}
                    onDelete={
                      !isSavedDoc || job?.job_status === "DRAFT"
                        ? () => {
                            if (isSavedDoc) {
                              deleteDocumentsMutate({ docId: d.id, jobId: job?.id });
                            } else {
                              setNewDocuments((prevState) => {
                                return prevState.filter((p) => p.name !== d.name);
                              });
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <FileUploader
                disabled={job?.job_status !== "DRAFT"}
                withPreview={false}
                setFile={(file) => {
                  setNewDocuments((prevState) => [...prevState, file]);
                }}
                description="PNG,JPG,PDF, DOCX"
                fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
                inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
              />
            </div>
          </div>
          <div className="flex-1 border-black border-solid pl-4 md:w-1/2 md:border-l">
            <Typography className="text-center" variant="display-xs" weight="semibold">
              Reference Documents
            </Typography>
            <div className="flex flex-wrap gap-4">
              {attachedNotesToShow.map((d, index) => {
                const isSavedDoc = "link" in d;
                return (
                  <AttachedDocument
                    key={index}
                    document={d}
                    onClick={() => {
                      if (isSavedDoc) {
                        window.open(d.link, "_blank")?.focus();
                      }
                    }}
                    onDelete={() => {
                      if (isSavedDoc) {
                        deleteDocumentsMutate({ docId: d.id, jobId: job?.id });
                      } else {
                        setNewReferenceDocuments((prevState) => {
                          return prevState.filter((p) => p.name !== d.name);
                        });
                      }
                    }}
                  />
                );
              })}
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <FileUploader
                className="flex-1"
                withPreview={false}
                setFile={(file) => {
                  setNewReferenceDocuments((prevState) => [...prevState, file]);
                }}
                description="PNG,JPG,PDF, DOCX"
                fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
                inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
              />
            </div>
          </div>
        </div>
        <Button className="self-center md:self-start" onClick={addNewDocs}>
          Upload all new files
        </Button>
      </div>
      <DeleteJobModal jobIdToRemove={jobIdToRemove} setJobIdToRemove={setJobIdToRemove} />
      <DuplicateJobModal
        jobIdToDuplicate={jobIdToDuplicate}
        setJobIdToDuplicate={setJobIdToDuplicate}
        job={job || null}
      />
      <NoCreditsModal isOpen={showNoCredits} setIsOpen={setShowNoCredits} />
      <Notification isOpen={!!reportError} onClose={() => setReportError("")} status="error">
        {reportError}
      </Notification>
    </div>
  );
}

type JobCardProps = {
  title: string;
  content: Record<string, string | number | undefined | boolean>;
};

function JobCard({ title, content }: JobCardProps) {
  // Filter out empty, null, undefined, or "NA" values to show only meaningful data
  const validEntries = Object.entries(content).filter(([_, value]) => {
    if (value == null || value === "") return false;
    const valueStr = value?.toString()?.trim().toLowerCase();
    return valueStr && !/^(n\/?a|-)$/.test(valueStr);
  });

  // Hide the entire card if no valid data remains
  if (validEntries.length === 0) return null;

  return (
    <div className="min-w-[250px] flex-1 overflow-hidden rounded-[10px] border border-success-300 border-solid p-6">
      <Typography variant="text-xl" weight="semibold" className="mb-4 text-gray-800">
        {title}
      </Typography>
      <div className="flex flex-col gap-3">
        {validEntries.map(([key, value]) => {
          const displayKey = isNumeric(key) ? "" : `${key}:`;
          const displayValue = typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
          return (
            <div key={key} className="flex flex-col gap-1">
              {displayKey && (
                <Typography variant="text-small" weight="semibold" className="text-gray-600">
                  {displayKey}
                </Typography>
              )}
              <Typography variant="text-small" className="overflow-hidden break-words break-all text-gray-800">
                {displayValue}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
}
